import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ConfirmButton from "@/components/ConfirmButton";

const ROLE_LABEL = { owner: "Titular", member: "Operador", viewer: "Visualizador" };

/** Generate a 12-char password with uppercase, lowercase, digit, and symbol. */
function _genPwd() {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";  // I, O excluded (look alike)
  const lower = "abcdefghijkmnopqrstuvwxyz"; // l excluded
  const digit = "23456789";                  // 0, 1 excluded
  const sym = "!@#$%&*";
  const pool = upper + lower + digit + sym;
  const rand = (s) => s[Math.floor(Math.random() * s.length)];
  // Guarantee at least one of each class
  const base = [rand(upper), rand(lower), rand(digit), rand(sym)];
  for (let i = 0; i < 8; i++) base.push(rand(pool));
  return base.sort(() => Math.random() - 0.5).join("");
}

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "member", manual_password: "" });
  const [manualMode, setManualMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingSlug, setEditingSlug] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [inviteResult, setInviteResult] = useState(null);  // {email, link?, manual_mode, password?} shown after invite

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/team");
      setData(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setData({ base: null, members: [], limit: 10, count: 0, _no_company: true });
      } else {
        toast.error(err.response?.data?.detail || err.message);
      }
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (manualMode && (form.manual_password || "").length < 8) {
      toast.error("Senha manual precisa ter pelo menos 8 caracteres.");
      return;
    }
    setBusy(true);
    try {
      const payload = manualMode
        ? { email: form.email, name: form.name, role: form.role, send_invite: false, manual_password: form.manual_password }
        : { email: form.email, name: form.name, role: form.role };
      const { data: res } = await api.post("/team/invite", payload);
      const successMsg = manualMode
        ? `Membro ${res.member.email} cadastrado com senha definida`
        : `Membro ${res.member.email} adicionado à equipe`;
      toast.success(successMsg);
      // Show success modal with either the password (manual) or invite link (email)
      setInviteResult({
        email: res.member.email,
        link: res.invite_link,
        manual_mode: manualMode,
        password: manualMode ? form.manual_password : null,
      });
      setShowInvite(false);
      setForm({ email: "", name: "", role: "member", manual_password: "" });
      setManualMode(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyInviteLink = async () => {
    const text = inviteResult.manual_mode
      ? `E-mail: ${inviteResult.email}\nSenha: ${inviteResult.password}\nLink de acesso: ${window.location.origin}/login`
      : inviteResult.link;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado para a área de transferência");
    } catch {
      toast.error("Não foi possível copiar. Selecione e copie manualmente.");
    }
  };

  const remove = async (uid) => {
    try {
      await api.delete(`/team/${uid}`);
      toast.success("Membro removido");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const saveSlug = async () => {
    if (!data?.base?.id) return;
    try {
      await api.put(`/companies/${data.base.id}/base-slug`, { base_slug: newSlug.trim().toLowerCase() });
      toast.success("Identificador da base atualizado");
      setEditingSlug(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  if (!data) {
    return <div className="p-12 text-center text-slate-500"><i className="fa-solid fa-circle-notch fa-spin mr-2"></i> Carregando…</div>;
  }

  if (data._no_company) {
    return (
      <div className="p-12 max-w-3xl mx-auto so-fade-in">
        <div className="card text-center py-12" data-testid="team-no-company">
          <div
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
            style={{ background: "var(--so-primary-soft)" }}
          >
            <i className="fa-solid fa-building text-2xl" style={{ color: "var(--so-primary)" }}></i>
          </div>
          <h2 className="font-display text-2xl font-extrabold text-slate-900 mb-2">Sua base ainda não foi criada</h2>
          <p className="text-slate-600 mb-5">
            Cadastre sua empresa primeiro — ela se torna automaticamente a base da sua conta e permite convidar até 10 usuários.
          </p>
          <a href="/app/empresas" className="btn btn-primary inline-flex" data-testid="team-no-company-cta">
            <i className="fa-solid fa-plus"></i> Cadastrar empresa
          </a>
        </div>
      </div>
    );
  }

  const isOwner = data.base?.owner_id === user?.id || user?.role === "admin";
  const remaining = data.limit - data.count;
  const seatPct = Math.min(100, (data.count / data.limit) * 100);

  return (
    <div className="p-8 lg:p-12 max-w-5xl mx-auto so-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Equipe</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">Membros da sua base</h1>
          <p className="text-slate-600 mt-2">
            Convide até <strong>{data.limit}</strong> usuários para compartilhar acesso a esta base e suas avaliações.
          </p>
        </div>
        {isOwner && (
          <button
            onClick={() => setShowInvite(true)}
            disabled={remaining <= 0 || user?.is_trial}
            className="btn btn-primary"
            data-testid="team-invite-button"
          >
            <i className="fa-solid fa-user-plus"></i> Convidar membro
          </button>
        )}
      </div>

      {/* Base identity card */}
      <div className="card mb-6" data-testid="team-base-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="overline mb-1.5">Identificador da base</div>
            {editingSlug ? (
              <div className="flex items-center gap-2">
                <input
                  className="input max-w-xs h-11"
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  placeholder="sollute"
                  data-testid="team-slug-input"
                />
                <button onClick={saveSlug} className="btn btn-primary" data-testid="team-slug-save">
                  <i className="fa-solid fa-check"></i>
                </button>
                <button onClick={() => setEditingSlug(false)} className="btn btn-secondary" data-testid="team-slug-cancel">
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <code
                  className="text-lg font-bold font-mono px-3 py-1.5 rounded-lg"
                  style={{ background: "var(--so-primary-soft)", color: "var(--so-primary)" }}
                  data-testid="team-slug-display"
                >
                  {data.base.slug}
                </code>
                {isOwner && (
                  <button
                    onClick={() => { setNewSlug(data.base.slug); setEditingSlug(true); }}
                    className="text-xs font-semibold hover:underline"
                    style={{ color: "var(--so-primary)" }}
                    data-testid="team-slug-edit"
                  >
                    <i className="fa-solid fa-pen-to-square mr-1"></i> Editar
                  </button>
                )}
              </div>
            )}
            <div className="text-xs text-slate-500 mt-2">
              Esse é o identificador que seus usuários digitam no campo <em>Cliente (Base)</em> da tela de login.
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="overline mb-1.5">Assentos</div>
            <div className="kpi-number text-3xl" style={{ color: "var(--so-primary)" }} data-testid="team-seats-count">
              {data.count}<span className="text-slate-300">/{data.limit}</span>
            </div>
            <div className="w-40 h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
              <div
                className="h-full transition-all"
                style={{ width: `${seatPct}%`, background: "var(--so-gradient)" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Trial gate hint */}
      {user?.is_trial && (
        <div
          className="card mb-6 border-amber-200"
          style={{ background: "#FFFBEB" }}
          data-testid="team-trial-banner"
        >
          <div className="flex items-start gap-3">
            <i className="fa-solid fa-crown text-amber-700 text-xl mt-0.5"></i>
            <div className="text-sm">
              <div className="font-bold text-amber-900 mb-0.5">Convites são exclusivos do plano completo</div>
              <div className="text-amber-800">
                Adquira o plano para liberar a inclusão de até {data.limit} membros na sua base.{" "}
                <a href="/app/planos" className="font-bold underline">Ver planos</a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Members table */}
      <div className="card" data-testid="team-members-card">
        <table className="so-table">
          <thead>
            <tr>
              <th className="pl-6">Nome</th>
              <th>E-mail</th>
              <th>Função</th>
              <th>Status</th>
              {isOwner && <th className="text-right pr-6"></th>}
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.id} data-testid={`team-row-${m.id}`}>
                <td className="pl-6 font-semibold text-slate-900">{m.name}</td>
                <td className="text-slate-600">{m.email}</td>
                <td>
                  <span
                    className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
                    style={
                      m.role === "owner"
                        ? { background: "var(--so-primary-soft)", color: "var(--so-primary)" }
                        : { background: "#F1F5F9", color: "#475569" }
                    }
                  >
                    {ROLE_LABEL[m.role] || m.role}
                  </span>
                </td>
                <td>
                  {m.invite_pending ? (
                    <span className="text-xs text-amber-700 font-semibold">
                      <i className="fa-solid fa-clock mr-1"></i> Convite pendente
                    </span>
                  ) : m.active ? (
                    <span className="text-xs text-emerald-700 font-semibold">
                      <i className="fa-solid fa-circle-check mr-1"></i> Ativo
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400 font-semibold">Inativo</span>
                  )}
                </td>
                {isOwner && (
                  <td className="text-right pr-6">
                    {m.role !== "owner" && (
                      <ConfirmButton
                        onConfirm={() => remove(m.id)}
                        testid={`team-remove-${m.id}`}
                      />
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invite modal */}
      {showInvite && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4"
          onClick={() => setShowInvite(false)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={invite}
            className="bg-white w-full max-w-md p-8 rounded-2xl shadow-2xl"
            data-testid="team-invite-modal"
          >
            <div className="overline mb-2">Novo convite</div>
            <h2 className="font-display text-2xl font-extrabold mb-6 tracking-tight">Convidar membro</h2>
            <label className="label">Nome completo *</label>
            <input
              required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input mb-4" placeholder="Maria Silva" data-testid="team-invite-name"
            />
            <label className="label">E-mail *</label>
            <input
              required type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input mb-4" placeholder="maria@empresa.com.br" data-testid="team-invite-email"
            />
            <label className="label">Função na base</label>
            <select
              value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="input mb-4" data-testid="team-invite-role"
            >
              <option value="member">Operador (cria e edita avaliações)</option>
              <option value="viewer">Visualizador (apenas leitura)</option>
            </select>

            <div className="border-t border-slate-200 pt-4 mb-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={manualMode}
                       onChange={(e) => setManualMode(e.target.checked)}
                       className="mt-1 h-4 w-4 accent-emerald-600"
                       data-testid="team-invite-manual-toggle" />
                <div>
                  <div className="text-sm font-semibold text-slate-900">Cadastro manual (sem e-mail)</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Define a senha aqui mesmo e compartilha com o membro via WhatsApp/Slack.
                    Útil quando o e-mail está bouncing ou o domínio ainda não está verificado.
                  </div>
                </div>
              </label>
              {manualMode && (
                <div className="mt-3 pl-7" data-testid="team-invite-manual-password-block">
                  <label className="label">Senha inicial (mínimo 8 caracteres)</label>
                  <input
                    type="text"
                    value={form.manual_password}
                    onChange={(e) => setForm({ ...form, manual_password: e.target.value })}
                    placeholder="Ex: Sollute@2026"
                    className="input mb-2"
                    data-testid="team-invite-manual-password"
                    minLength={8}
                  />
                  <button type="button"
                          onClick={() => setForm({ ...form, manual_password: _genPwd() })}
                          className="text-xs text-emerald-700 hover:underline"
                          data-testid="team-invite-gen-password">
                    <i className="fa-solid fa-arrows-rotate"></i> Gerar senha segura aleatória
                  </button>
                  <p className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-200 px-3 py-2">
                    <i className="fa-solid fa-circle-info"></i> O membro deverá trocar a senha no primeiro login.
                  </p>
                </div>
              )}
            </div>

            <div className="text-xs text-slate-500 mb-6">
              {manualMode
                ? "Nenhum e-mail será enviado. Você compartilha a senha pessoalmente."
                : "O convite é enviado por e-mail com link para definir senha."}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button" onClick={() => { setShowInvite(false); setManualMode(false); }}
                className="btn btn-secondary" data-testid="team-invite-cancel"
              >Cancelar</button>
              <button
                type="submit" disabled={busy}
                className="btn btn-primary" data-testid="team-invite-submit"
              >
                {busy
                  ? <><i className="fa-solid fa-circle-notch fa-spin"></i> {manualMode ? "Cadastrando…" : "Enviando…"}</>
                  : manualMode
                    ? <><i className="fa-solid fa-user-plus"></i> Cadastrar membro</>
                    : <><i className="fa-solid fa-paper-plane"></i> Enviar convite</>
                }
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Success modal: shows either manual credentials OR the invite link fallback */}
      {inviteResult && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
             data-testid="invite-link-modal">
          <div className="card w-full max-w-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                <i className="fa-solid fa-check text-emerald-700"></i>
              </div>
              <div>
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
                  {inviteResult.manual_mode ? "Membro cadastrado!" : "Membro adicionado!"}
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  {inviteResult.manual_mode ? (
                    <>Cadastro manual concluído para <strong>{inviteResult.email}</strong>. Compartilha as
                    credenciais abaixo com o membro (WhatsApp, Slack, pessoalmente, etc.).
                    Ele será solicitado a trocar a senha no primeiro login.</>
                  ) : (
                    <>Enviamos um e-mail para <strong>{inviteResult.email}</strong> com o link de definição
                    de senha. Caso o e-mail não chegue (spam, domínio não verificado no Resend, etc.), use
                    o link manual abaixo:</>
                  )}
                </p>
              </div>
            </div>

            {inviteResult.manual_mode ? (
              <div className="bg-emerald-50 border border-emerald-300 p-4 mb-4">
                <div className="text-xs text-emerald-800 font-bold uppercase tracking-wider mb-2">Credenciais</div>
                <div className="font-mono text-sm space-y-1.5">
                  <div><span className="text-slate-500 text-xs">URL:</span>{" "}
                    <span className="font-bold text-slate-900 select-all">{window.location.origin}/login</span></div>
                  <div><span className="text-slate-500 text-xs">E-mail:</span>{" "}
                    <span className="font-bold text-slate-900 select-all">{inviteResult.email}</span></div>
                  <div><span className="text-slate-500 text-xs">Senha:</span>{" "}
                    <span className="font-bold text-slate-900 select-all" data-testid="invite-shown-password">
                      {inviteResult.password}
                    </span></div>
                </div>
              </div>
            ) : (
              inviteResult.link && (
                <div className="bg-slate-50 border border-slate-200 p-3 font-mono text-xs break-all mb-4 select-all"
                     data-testid="invite-link-text">
                  {inviteResult.link}
                </div>
              )
            )}

            <div className="flex gap-2 justify-end">
              <button onClick={copyInviteLink} className="btn btn-secondary"
                      data-testid="copy-invite-link">
                <i className="fa-solid fa-copy"></i> {inviteResult.manual_mode ? "Copiar credenciais" : "Copiar link"}
              </button>
              <button onClick={() => setInviteResult(null)} className="btn btn-primary"
                      data-testid="close-invite-link-modal">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
